import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api/config.js';
import { processApiData } from '../../services/api/utils.js';
import TokenLogo from './TokenLogo';

const SearchDropdown = ({ onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [tokenList, setTokenList] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch token list on component mount
  useEffect(() => {
    const fetchTokens = async () => {
      try {
        setIsLoading(true);
        console.log('Building token list for search dropdown');
        
        // Start with hardcoded tokens that have logos (prioritized)
        const priorityTokens = [
          {
            address: '0xb7cf94eE2b010ff69EA4F67a7D951c0Cd8936170',
            symbol: '3DC',
            name: '3D City',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x18ba8B66cF799C23AE5c3728aD3D4d9017213149',
            symbol: 'WSTO',
            name: 'Wrapped Studio',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x670E09c73f34bA775a0e5422Df6cF09c9dD5Cabc',
            symbol: 'ADA',
            name: 'Cardano',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x7086c7C9108E0AC7736fcBF19bf0937ef019B8f3',
            symbol: 'TRX',
            name: 'TRON',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x76eAa2c476dB78AD503CE24C4CF3c595651aBB47',
            symbol: 'BTC',
            name: 'Bitcoin',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xD9C4be029295e717B23D6911444b0e5e830CD959',
            symbol: 'LINK',
            name: 'Chainlink',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x0FFf976C1a92413c89FD0a5da55A46091A19dB14',
            symbol: 'AVAX',
            name: 'Avalanche',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xB267b51A12e54e4F4ec29DC142ebC4121f78B58E',
            symbol: 'XLM',
            name: 'Stellar',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x5B1E2036701BBb5bD26B073A2d4C23d42629efC0',
            symbol: 'TON',
            name: 'Toncoin',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x5e34d88740791F5D4539f2Ee1f21d745fD105c7a',
            symbol: 'DOT',
            name: 'Polkadot',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x2575054bDe53C8E6B1eF4c065fCd9eaFac686Fc9',
            symbol: 'LTC',
            name: 'Litecoin',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x6A5Bd07bae7F3A118625F53F137578F2186aB10f',
            symbol: 'PEPE',
            name: 'Pepe',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x502e28a890379E14c1AC48C8211a17dE90aB4908',
            symbol: 'DAI',
            name: 'Dai Stablecoin',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xf02525BfF49F68Ba5b212101b6e42fAbeAF51929',
            symbol: 'AAVE',
            name: 'Aave',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x17774452905C45C21a4DFc5607040093e9Cc0B07',
            symbol: 'NEAR',
            name: 'NEAR Protocol',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xaf9FC8a635DcA9F0969071BB29D6c7911cbD2B25',
            symbol: 'FET',
            name: 'Fetch.ai',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x40FA86B63e0A4F6d6480D2a615CbE8aeC9593b2D',
            symbol: 'VET',
            name: 'VeChain',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xcd7394D30e2cC8183Aae24a3173D2A19e18c2a72',
            symbol: 'ARB',
            name: 'Arbitrum',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x952d8d93dCB730450c1B56E1a6894a83bf06fFE4',
            symbol: 'OP',
            name: 'Optimism',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xC718a5955C595D757A7f2e77dFbAc6dc867029a7',
            symbol: 'USD1',
            name: 'USD1',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x289EE4618520b2Fe8f2763525F3114A71D8D4Ff7',
            symbol: 'ETH',
            name: 'Ethereum',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x6E7CCe539b9b720e3d483bb3e68C0366CCdE2f34',
            symbol: 'WBNB',
            name: 'Wrapped BNB',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x416a4b8365DEAF14452cf40c73065A4BC1EbF066',
            symbol: 'USDT',
            name: 'Tether USD',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
            symbol: 'USDC',
            name: 'USD Coin',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x8332c045587d5d9ff3d5eec2fb21e417166b0ede',
            symbol: 'COOP',
            name: 'The Coop Network',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0xce6c09a662438a555af258a5fc46e6fbc1b368d3',
            symbol: 'COOP-S',
            name: 'COOP-S Token',
            hasLogo: true,
            priority: 1
          },
          {
            address: '0x648c7e61e11e2eb2750028af6b58cbc34fa35432',
            symbol: 'GMDS',
            name: 'GMDS Token',
            hasLogo: true,
            priority: 1
          }
        ];
        
        // Try to discover additional tokens from recent transactions
        const discoveredTokens = [];
        
        try {
          console.log('Discovering tokens from recent transactions...');
          
          // Get recent transactions to discover tokens
          const recentTransactions = await api.get('/transactions', {
            params: { limit: 100 }
          });
          const transactions = processApiData(recentTransactions.data);
          
          // Extract unique token addresses from token transfers
          const tokenAddresses = new Set();
          
          if (Array.isArray(transactions)) {
            transactions.forEach(tx => {
              if (tx.tokenTransfers && Array.isArray(tx.tokenTransfers)) {
                tx.tokenTransfers.forEach(transfer => {
                  if (transfer.tokenAddress && transfer.tokenAddress !== '0x0000000000000000000000000000000000000000') {
                    tokenAddresses.add(transfer.tokenAddress.toLowerCase());
                  }
                });
              }
            });
          }
          
          console.log(`Found ${tokenAddresses.size} unique token addresses from recent transactions`);
          
          // For each discovered token address, try to get token details
          for (const tokenAddress of tokenAddresses) {
            try {
              // Skip if we already have this token in priority list
              const alreadyExists = priorityTokens.some(token => 
                token.address.toLowerCase() === tokenAddress.toLowerCase()
              );
              
              if (alreadyExists) {
                continue;
              }
              
              // Try to get token details using contract calls
              const nameResponse = await api.post('/proxy/rpc', {
                jsonrpc: '2.0',
                method: 'eth_call',
                params: [
                  {
                    to: tokenAddress,
                    data: '0x06fdde03' // name()
                  },
                  'latest'
                ],
                id: 1
              });
              
              const symbolResponse = await api.post('/proxy/rpc', {
                jsonrpc: '2.0',
                method: 'eth_call',
                params: [
                  {
                    to: tokenAddress,
                    data: '0x95d89b41' // symbol()
                  },
                  'latest'
                ],
                id: 1
              });
              
              // Decode the responses
              let name = 'Unknown Token';
              let symbol = 'TOKEN';
              
              if (nameResponse.data && nameResponse.data.result && nameResponse.data.result !== '0x') {
                try {
                  // Simple string decoding
                  const nameHex = nameResponse.data.result;
                  if (nameHex.length > 130) {
                    const nameData = nameHex.slice(130);
                    name = '';
                    for (let i = 0; i < nameData.length; i += 2) {
                      const charCode = parseInt(nameData.substr(i, 2), 16);
                      if (charCode !== 0) {
                        name += String.fromCharCode(charCode);
                      }
                    }
                    name = name.trim() || 'Unknown Token';
                  }
                } catch (error) {
                  console.error('Error decoding token name:', error);
                }
              }
              
              if (symbolResponse.data && symbolResponse.data.result && symbolResponse.data.result !== '0x') {
                try {
                  // Simple string decoding
                  const symbolHex = symbolResponse.data.result;
                  if (symbolHex.length > 130) {
                    const symbolData = symbolHex.slice(130);
                    symbol = '';
                    for (let i = 0; i < symbolData.length; i += 2) {
                      const charCode = parseInt(symbolData.substr(i, 2), 16);
                      if (charCode !== 0) {
                        symbol += String.fromCharCode(charCode);
                      }
                    }
                    symbol = symbol.trim() || 'TOKEN';
                  }
                } catch (error) {
                  console.error('Error decoding token symbol:', error);
                }
              }
              
              // Add discovered token
              discoveredTokens.push({
                address: tokenAddress,
                symbol: symbol,
                name: name,
                hasLogo: false,
                priority: 2
              });
              
            } catch (error) {
              console.error(`Error fetching details for token ${tokenAddress}:`, error);
            }
          }
          
          console.log(`Successfully discovered ${discoveredTokens.length} additional tokens`);
          
        } catch (error) {
          console.error('Error discovering tokens from transactions:', error);
        }
        
        // Combine priority tokens and discovered tokens
        const allTokens = [...priorityTokens, ...discoveredTokens];
        
        // Sort by priority (priority tokens first), then by symbol
        allTokens.sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }
          return (a.symbol || '').localeCompare(b.symbol || '');
        });
        
        console.log(`Built token list with ${allTokens.length} tokens (${priorityTokens.length} priority, ${discoveredTokens.length} discovered)`);
        setTokenList(allTokens);
        setIsLoading(false);
        
      } catch (error) {
        console.error('Error building token list for search dropdown:', error);
        setTokenList([]);
        setIsLoading(false);
      }
    };

    fetchTokens();
  }, []);

  // Filter suggestions based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);

    // Enhanced search function that includes real-time blockchain discovery
    const performSearch = async () => {
      const query = searchQuery.toLowerCase();
      
      // Check if the query looks like a contract address (0x followed by 40 hex characters)
      const isContractAddress = /^0x[a-fA-F0-9]{40}$/.test(query);
      
      // First, filter existing tokens
      const existingMatches = tokenList.filter(token => 
        (token.name && token.name.toLowerCase().includes(query)) || 
        (token.symbol && token.symbol.toLowerCase().includes(query)) ||
        (token.address && token.address.toLowerCase().includes(query))
      );
      
      // If the query is a contract address, try to fetch token details directly
      if (isContractAddress) {
        console.log(`Detected contract address: ${query}`);
        
        try {
          // Check if we already have this token
          const existingToken = tokenList.find(token => 
            token.address.toLowerCase() === query
          );
          
          if (existingToken) {
            setSuggestions([existingToken]);
            setShowDropdown(true);
            setIsLoading(false);
            return;
          }
          
          // Try to fetch token details directly from the contract
          const nameResponse = await api.post('/proxy/rpc', {
            jsonrpc: '2.0',
            method: 'eth_call',
            params: [
              {
                to: query,
                data: '0x06fdde03' // name()
              },
              'latest'
            ],
            id: 1
          });
          
          const symbolResponse = await api.post('/proxy/rpc', {
            jsonrpc: '2.0',
            method: 'eth_call',
            params: [
              {
                to: query,
                data: '0x95d89b41' // symbol()
              },
              'latest'
            ],
            id: 1
          });
          
          const decimalsResponse = await api.post('/proxy/rpc', {
            jsonrpc: '2.0',
            method: 'eth_call',
            params: [
              {
                to: query,
                data: '0x313ce567' // decimals()
              },
              'latest'
            ],
            id: 1
          });
          
          // Decode the responses
          let name = 'Unknown Token';
          let symbol = 'TOKEN';
          let decimals = 18;
          
          if (nameResponse.data && nameResponse.data.result && nameResponse.data.result !== '0x') {
            try {
              const nameHex = nameResponse.data.result;
              if (nameHex.length > 130) {
                const nameData = nameHex.slice(130);
                name = '';
                for (let i = 0; i < nameData.length; i += 2) {
                  const charCode = parseInt(nameData.substr(i, 2), 16);
                  if (charCode !== 0) {
                    name += String.fromCharCode(charCode);
                  }
                }
                name = name.trim() || 'Unknown Token';
              }
            } catch (error) {
              console.error('Error decoding token name:', error);
            }
          }
          
          if (symbolResponse.data && symbolResponse.data.result && symbolResponse.data.result !== '0x') {
            try {
              const symbolHex = symbolResponse.data.result;
              if (symbolHex.length > 130) {
                const symbolData = symbolHex.slice(130);
                symbol = '';
                for (let i = 0; i < symbolData.length; i += 2) {
                  const charCode = parseInt(symbolData.substr(i, 2), 16);
                  if (charCode !== 0) {
                    symbol += String.fromCharCode(charCode);
                  }
                }
                symbol = symbol.trim() || 'TOKEN';
              }
            } catch (error) {
              console.error('Error decoding token symbol:', error);
            }
          }
          
          if (decimalsResponse.data && decimalsResponse.data.result && decimalsResponse.data.result !== '0x') {
            try {
              decimals = parseInt(decimalsResponse.data.result, 16);
            } catch (error) {
              console.error('Error decoding token decimals:', error);
            }
          }
          
          // If we successfully got token details, show it
          if (name !== 'Unknown Token' || symbol !== 'TOKEN') {
            const discoveredToken = {
              address: query,
              symbol: symbol,
              name: name,
              decimals: decimals,
              hasLogo: false,
              priority: 3
            };
            
            console.log(`Successfully discovered token from contract address: ${name} (${symbol})`);
            setSuggestions([discoveredToken]);
            setShowDropdown(true);
            setIsLoading(false);
            return;
          } else {
            console.log(`Contract address ${query} does not appear to be a valid ERC20 token`);
            // Still show existing matches if any
            setSuggestions(existingMatches.slice(0, 5));
            setShowDropdown(existingMatches.length > 0);
            setIsLoading(false);
            return;
          }
          
        } catch (error) {
          console.error(`Error fetching token details for contract address ${query}:`, error);
          // Fallback to existing matches
          setSuggestions(existingMatches.slice(0, 5));
          setShowDropdown(existingMatches.length > 0);
          setIsLoading(false);
          return;
        }
      }
      
      // If we have good matches from existing tokens, use them
      if (existingMatches.length >= 3) {
        setSuggestions(existingMatches.slice(0, 5));
        setShowDropdown(true);
        setIsLoading(false);
        return;
      }
      
      // If not enough matches, try to discover more tokens from blockchain
      try {
        console.log(`Performing enhanced search for: ${query}`);
        
        // Get more recent transactions to find additional tokens
        const moreTransactions = await api.get('/transactions', {
          params: { limit: 200 } // Get more transactions for better discovery
        });
        const transactions = processApiData(moreTransactions.data);
        
        const discoveredTokens = [];
        const tokenAddresses = new Set();
        
        if (Array.isArray(transactions)) {
          transactions.forEach(tx => {
            if (tx.tokenTransfers && Array.isArray(tx.tokenTransfers)) {
              tx.tokenTransfers.forEach(transfer => {
                if (transfer.tokenAddress && transfer.tokenAddress !== '0x0000000000000000000000000000000000000000') {
                  tokenAddresses.add(transfer.tokenAddress.toLowerCase());
                }
              });
            }
          });
        }
        
        // Check each discovered token to see if it matches the search query
        for (const tokenAddress of tokenAddresses) {
          try {
            // Skip if we already have this token
            const alreadyExists = tokenList.some(token => 
              token.address.toLowerCase() === tokenAddress.toLowerCase()
            );
            
            if (alreadyExists) {
              continue;
            }
            
            // Try to get token details
            const nameResponse = await api.post('/proxy/rpc', {
              jsonrpc: '2.0',
              method: 'eth_call',
              params: [
                {
                  to: tokenAddress,
                  data: '0x06fdde03' // name()
                },
                'latest'
              ],
              id: 1
            });
            
            const symbolResponse = await api.post('/proxy/rpc', {
              jsonrpc: '2.0',
              method: 'eth_call',
              params: [
                {
                  to: tokenAddress,
                  data: '0x95d89b41' // symbol()
                },
                'latest'
              ],
              id: 1
            });
            
            // Decode the responses
            let name = 'Unknown Token';
            let symbol = 'TOKEN';
            
            if (nameResponse.data && nameResponse.data.result && nameResponse.data.result !== '0x') {
              try {
                const nameHex = nameResponse.data.result;
                if (nameHex.length > 130) {
                  const nameData = nameHex.slice(130);
                  name = '';
                  for (let i = 0; i < nameData.length; i += 2) {
                    const charCode = parseInt(nameData.substr(i, 2), 16);
                    if (charCode !== 0) {
                      name += String.fromCharCode(charCode);
                    }
                  }
                  name = name.trim() || 'Unknown Token';
                }
              } catch (error) {
                console.error('Error decoding token name:', error);
              }
            }
            
            if (symbolResponse.data && symbolResponse.data.result && symbolResponse.data.result !== '0x') {
              try {
                const symbolHex = symbolResponse.data.result;
                if (symbolHex.length > 130) {
                  const symbolData = symbolHex.slice(130);
                  symbol = '';
                  for (let i = 0; i < symbolData.length; i += 2) {
                    const charCode = parseInt(symbolData.substr(i, 2), 16);
                    if (charCode !== 0) {
                      symbol += String.fromCharCode(charCode);
                    }
                  }
                  symbol = symbol.trim() || 'TOKEN';
                }
              } catch (error) {
                console.error('Error decoding token symbol:', error);
              }
            }
            
            // Check if this token matches the search query
            const tokenMatches = 
              (name.toLowerCase().includes(query)) ||
              (symbol.toLowerCase().includes(query)) ||
              (tokenAddress.toLowerCase().includes(query));
            
            if (tokenMatches) {
              discoveredTokens.push({
                address: tokenAddress,
                symbol: symbol,
                name: name,
                hasLogo: false,
                priority: 3 // Lower priority than existing tokens
              });
            }
            
            // Limit the number of discovered tokens to avoid too many API calls
            if (discoveredTokens.length >= 3) {
              break;
            }
            
          } catch (error) {
            console.error(`Error fetching details for token ${tokenAddress}:`, error);
          }
        }
        
        // Combine existing matches with discovered tokens
        const allMatches = [...existingMatches, ...discoveredTokens];
        
        // Sort by priority (existing tokens first, then discovered)
        allMatches.sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }
          return (a.symbol || '').localeCompare(b.symbol || '');
        });
        
        setSuggestions(allMatches.slice(0, 5));
        setShowDropdown(allMatches.length > 0);
        
        console.log(`Enhanced search found ${allMatches.length} matches (${existingMatches.length} existing, ${discoveredTokens.length} discovered)`);
        
      } catch (error) {
        console.error('Error in enhanced search:', error);
        // Fallback to existing matches only
        setSuggestions(existingMatches.slice(0, 5));
        setShowDropdown(existingMatches.length > 0);
      }
      
      setIsLoading(false);
    };

    // Debounce search to avoid excessive API calls
    const debounceTimer = setTimeout(performSearch, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, tokenList]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Close dropdown
    setShowDropdown(false);
    
    // Call onSearch callback
    if (onSearch) {
      onSearch(searchQuery);
    }

    // Navigate to search page
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    setSearchQuery('');
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    // Navigate to address page for tokens (since token pages are being removed)
    if (suggestion.address) {
      navigate(`/address/${suggestion.address}`);
    } else {
      // Otherwise, perform a regular search
      navigate(`/search?q=${encodeURIComponent(suggestion.name || suggestion)}`);
    }
    
    // Close dropdown and clear search
    setShowDropdown(false);
    setSearchQuery('');
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    }
    // Arrow up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
    // Enter
    else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[focusedIndex]);
    }
    // Escape
    else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Helper function to truncate address
  const truncateAddress = (address, length = 6) => {
    if (!address) return '';
    return `${address.substring(0, length)}...${address.substring(address.length - 4)}`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <form onSubmit={handleSearch} className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by Block / Txn / Address / Token"
          className="w-full md:w-64 lg:w-80 px-4 py-2 bg-dark-200 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setShowDropdown(searchQuery.trim().length > 0)}
          onKeyDown={handleKeyDown}
        />
        <button 
          type="submit" 
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </form>

      {/* Suggestions Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full bg-dark-100 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
          {isLoading ? (
            <div className="p-3 text-center text-gray-400">
              <div className="inline-block w-4 h-4 border-2 border-gray-400 border-t-primary rounded-full animate-spin mr-2"></div>
              Loading...
            </div>
          ) : suggestions.length > 0 ? (
            <ul>
              {suggestions.map((suggestion, index) => (
                <li 
                  key={suggestion.address || index}
                  className={`px-4 py-2 cursor-pointer hover:bg-dark-200 ${focusedIndex === index ? 'bg-dark-200' : ''}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className="flex items-center">
                    {suggestion.symbol && (
                      <TokenLogo token={suggestion} size="sm" className="mr-2 flex-shrink-0" />
                    )}
                    <div className="overflow-hidden">
                      <div className="font-medium text-white truncate">
                        {suggestion.name || suggestion}
                        {suggestion.symbol && <span className="ml-1 text-gray-400 text-sm">({suggestion.symbol})</span>}
                      </div>
                      {suggestion.address && (
                        <div className="text-xs text-gray-500 font-mono">
                          {truncateAddress(suggestion.address)}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-3 text-center text-gray-400">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
